import generationsApi from '../services/generationsApi'
import useAuthStore from '../stores/authStore'

class ImageLoadingQueue {
  constructor() {
    this.visibleQueue = new Set()
    this.processingQueue = new Set()
    this.maxConcurrent = 3
    this.processQueue()
  }

  addToVisibleQueue(imageId, priority = false) {
    if (this.processingQueue.has(imageId)) return
    
    if (priority) {
      this.visibleQueue.delete(imageId)
      this.visibleQueue = new Set([imageId, ...Array.from(this.visibleQueue)])
    } else {
      this.visibleQueue.add(imageId)
    }
    
    this.processQueue()
  }

  removeFromQueues(imageId) {
    this.visibleQueue.delete(imageId)
    this.processingQueue.delete(imageId)
  }

  async processQueue() {
    if (this.processingQueue.size >= this.maxConcurrent) return

    for (const imageId of this.visibleQueue) {
      if (this.processingQueue.size >= this.maxConcurrent) break
      
      if (!this.processingQueue.has(imageId)) {
        this.processingQueue.add(imageId)
        this.visibleQueue.delete(imageId)
        
        try {
          await this.loadImage(imageId)
        } finally {
          this.processingQueue.delete(imageId)
          this.processQueue()
        }
      }
    }
  }

  async loadImage(imageId) {
    const token = useAuthStore.getState().token
    
    if (!token) {
      console.warn('[ImageLoadingQueue] No token')
      return
    }

    console.debug('[ImageLoadingQueue] Loading image:', imageId)

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/generations/image/${imageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      
      console.debug('[ImageLoadingQueue] Image loaded successfully:', imageId)
      
      window.dispatchEvent(new CustomEvent(`imageLoaded:${imageId}`, {
        detail: { success: true, url: objectUrl }
      }))
    } catch (error) {
      console.error(`[ImageLoadingQueue] Failed to load image ${imageId}:`, error)
      
      window.dispatchEvent(new CustomEvent(`imageLoaded:${imageId}`, {
        detail: { success: false, error: error.message }
      }))
    }
  }

  getLoadedImage(imageId) {
    return this.loadedImages.get(imageId)
  }

  cleanup() {
    this.loadedImages.forEach(url => URL.revokeObjectURL(url))
    this.loadedImages.clear()
    this.processingQueue.clear()
    this.visibleQueue.clear()
  }
}

export const imageLoadingQueue = new ImageLoadingQueue() 